import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@nexply/ui';
import { UsersRound } from 'lucide-react';
import { api } from '../lib/api.js';
import { LegalFooter } from '../components/LegalFooter.js';

const STATUS_TEXT = {
  revoked: 'Dieser Einladungslink wurde zurückgezogen.',
  expired: 'Dieser Einladungslink ist abgelaufen.',
  used_up: 'Dieser Einladungslink wurde bereits so oft genutzt wie erlaubt.',
} as const;

/**
 * Einladung in eine Netzwerk-Gruppe. Öffentlich, damit man vor dem Anmelden sieht,
 * wohin man eingeladen wird. Beitreten selbst braucht ein Konto: Ohne Anmeldung geht es
 * über Login/Registrierung mit `?next=` hierher zurück.
 */
export function GroupInvitePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const nav = useNavigate();
  const qc = useQueryClient();
  const here = `/g/${token}`;

  const q = useQuery({
    queryKey: ['group-invite', token],
    queryFn: () => api.groups.previewInvite(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () => api.groups.acceptInvite(token),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['groups'] });
      nav({ to: '/groups/$groupId', params: { groupId: res.groupId } });
    },
  });

  const d = q.data;

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center overflow-hidden bg-ink px-6 pt-[calc(env(safe-area-inset-top)+48px)] pb-[calc(env(safe-area-inset-bottom)+24px)]">
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-gold/15 blur-[120px]" />
      <div className="relative z-10 w-full max-w-md flex-1">
        <div className="mb-8 text-center">
          <h1 className="text-5xl font-semibold tracking-tighter text-gold-grad">nexply</h1>
        </div>

        <div className="rounded-3xl border border-line bg-surface-1 p-6 shadow-card-lg animate-slide-up">
          {q.isLoading && <div className="h-40 rounded-2xl bg-raise animate-pulse" />}

          {q.isError && (
            <div className="space-y-4 text-center">
              <h2 className="text-xl font-semibold text-text">Einladung nicht gefunden</h2>
              <p className="text-sm text-text-dim">Der Link ist ungültig. Frag am besten nach einem neuen.</p>
              <Link to="/app">
                <Button variant="secondary" fullWidth>
                  Zu Nexply
                </Button>
              </Link>
            </div>
          )}

          {d && (
            <div className="space-y-5">
              <div className="flex flex-col items-center text-center">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-gold/10 text-gold">
                  <UsersRound className="h-7 w-7" />
                </div>
                <p className="mt-4 text-sm text-text-dim">{d.invitedBy} lädt dich ein in die Netzwerk-Gruppe</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-text">{d.group.name}</h2>
                {d.group.description && <p className="mt-2 text-sm text-text-dim">{d.group.description}</p>}
                <p className="mt-2 text-xs text-text-muted">
                  {d.group.memberCount} {d.group.memberCount === 1 ? 'Mitglied' : 'Mitglieder'} · wer beitritt, ist automatisch
                  mit allen vernetzt
                </p>
              </div>

              {d.alreadyMember ? (
                <Button fullWidth onClick={() => nav({ to: '/groups/$groupId', params: { groupId: d.group.id } })}>
                  Du bist schon drin: zur Gruppe
                </Button>
              ) : d.status !== 'valid' ? (
                <div className="rounded-xl border border-line bg-raise px-4 py-3 text-center text-sm text-text-dim">
                  {STATUS_TEXT[d.status]}
                </div>
              ) : d.loggedIn ? (
                <>
                  <Button fullWidth size="lg" loading={accept.isPending} onClick={() => accept.mutate()}>
                    Gruppe beitreten
                  </Button>
                  {accept.isError && (
                    <div className="text-center text-sm text-danger">
                      {(accept.error as Error).message || 'Beitreten hat nicht geklappt.'}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <Link to="/register" search={{ next: here }}>
                    <Button fullWidth size="lg">
                      Konto erstellen und beitreten
                    </Button>
                  </Link>
                  <Link to="/login" search={{ next: here }}>
                    <Button fullWidth variant="secondary">
                      Ich habe schon ein Konto
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <LegalFooter className="relative z-10 mt-8" />
    </div>
  );
}
